import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Button, Grid, TextField } from "@mui/material";
import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";
import { useLazyGetDDLDriverListQuery } from "store/services/driverApi";
import { useGetContractorUserDataDetailQuery } from "store/services/contractorUserApi";
import { getDateTimeAuto } from "utils/dateFormats";
import { useAuth } from "hooks/useAuth";

const INITIAL_STATE = {
  collectionId: "",
  shipmentId: "",
  vehicle_id: "",
  contractorId: "",
  driverId: "",
  actualPickupTime: getDateTimeAuto(),
  status: "Pending",
  isActive: true,
  account: "",
  entity: "",
  contractorProfile: null, 
};

const CollectionModalForm = ({
  isBusy = false,
  fulfillmentDetail = null,
  onSubmit = () => {},
  shipmentId = "",
  driverId = "",
  vehicle_id = "",
}) => {
  const navigate = useNavigate();
  const { viewEntity, isContractor, loggedInUser } = useAuth();

  const [fetchEntities] = useLazyGetDDLEntityListQuery();
  const [fetchDrivers, { data: drivers, isLoading: loadingDrivers }] =
    useLazyGetDDLDriverListQuery();

  const { data: contractorData, isLoading: loadingContractorData } =
    useGetContractorUserDataDetailQuery(loggedInUser?.typeRef, {
      skip: !isContractor || !loggedInUser?.typeRef,
    });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      account: viewEntity?.entity?.account || "",
      entity: viewEntity?.entity || "",
      contractorId: isContractor ? loggedInUser?.typeRef : "",
      shipmentId: shipmentId || "",
      driverId: driverId || "",
      vehicle_id: vehicle_id || "",
    },
  });

  const _watchAccount = useWatch({ control, name: "account" });
  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchDriver = useWatch({ control, name: "driverId" });
  const _watchContractorProfile = useWatch({ control, name: "contractorProfile" });

  const driversList = useMemo(
    () =>
      isContractor
        ? _watchContractorProfile?.driverId || []
        : drivers || [],
    [isContractor, _watchContractorProfile, drivers]
  );

  const vehiclesList = useMemo(
    () =>
      isContractor
        ? _watchContractorProfile?.vehicle_id || []
        : _watchDriver?.assigned_vehicle_ids || [],
    [isContractor, _watchContractorProfile, _watchDriver]
  );

  useEffect(() => {
    const accountId = _watchAccount?._id || viewEntity?.entity?.account?._id;
    if (accountId) {
      fetchEntities({ account: accountId });
    }
  }, [_watchAccount?._id, fetchEntities, viewEntity?.entity?.account]);

  useEffect(() => {
    if (!_watchEntity?._id || isContractor) return;
    const driverParams = {
      account: _watchAccount?._id || viewEntity?.entity?.account?._id,
      entity: _watchEntity?._id,
    };
    fetchDrivers(driverParams);
  }, [
    _watchAccount?._id,
    _watchEntity?._id,
    fetchDrivers,
    isContractor,
    viewEntity?.entity?.account,
  ]);

  useEffect(() => {
    if (fulfillmentDetail) {
      reset({ ...INITIAL_STATE, ...fulfillmentDetail });
    } else {
      reset({
        ...INITIAL_STATE,
        contractorId: isContractor ? loggedInUser?.typeRef : "",
        account: viewEntity?.entity?.account || "",
        entity: viewEntity?.entity || "",
        shipmentId: shipmentId || "",
        driverId: driverId || "",
        vehicle_id: vehicle_id || "",
      });
    }
  }, [
    fulfillmentDetail,
    reset,
    isContractor,
    loggedInUser,
    viewEntity?.entity,
    shipmentId,
    driverId,
    vehicle_id,
  ]);

  const handleOnSubmit = async (data) => {
    const { status, ...formattedData } = data;
  
    if (isContractor) {
      formattedData.contractorId = data.contractorProfile._id;
    }
    formattedData.shipmentId =
      shipmentId || data?.shipmentId?._id || data?.shipmentId;
    formattedData.vehicle_id = data?.vehicle_id?._id || data?.vehicle_id;
    formattedData.driverId = data?.driverId?._id || data?.driverId;
    formattedData.status = "Completed";
  
    formattedData.account =
      typeof viewEntity?.entity?.account === "object"
        ? viewEntity.entity.account._id || viewEntity.entity.account
        : viewEntity?.entity?.account;
    formattedData.entity =
      typeof viewEntity?.entity === "object"
        ? viewEntity.entity._id || viewEntity.entity
        : viewEntity?.entity;
  
    onSubmit(formattedData);
  };
  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          <Grid item md={6} sx={{ display: "none" }}>
            <Controller
              name="account"
              control={control}
              render={({ field }) => <input type="hidden" {...field} />}
            />
          </Grid>
          <Grid item md={6} sx={{ display: "none" }}>
            <Controller
              name="entity"
              control={control}
              render={({ field }) => <input type="hidden" {...field} />}
            />
          </Grid>

          {isContractor && (
            <Grid item md={6}>
              <Controller
                name="contractorProfile"
                control={control}
                rules={{ required: "Please select a contractor profile." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Accept as"
                    value={value || ""}
                    onChange={(_, data) => onChange(data)}
                    getOptionLabel={(option) => option?.name || ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={contractorData?.contractorProfiles || []}
                    loading={loadingContractorData}
                    loadingText="Loading Contractor Profiles..."
                    helperText={errors?.contractorProfile?.message}
                    error={!!errors?.contractorProfile}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={6}>
            <Controller
              name="driverId"
              control={control}
              rules={{ required: "Please select a driver." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Driver"
                  value={value || ""}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={driversList}
                  loading={
                    isContractor ? loadingContractorData : loadingDrivers
                  }
                  loadingText={
                    isContractor
                      ? "Loading Contractor Data..."
                      : "Loading Drivers..."
                  }
                  helperText={errors?.driverId?.message}
                  error={!!errors?.driverId}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="vehicle_id"
              control={control}
              rules={{ required: "Please select a vehicle." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  required
                  label="Vehicle"
                  value={value || ""}
                  onChange={(_, data) => onChange(data)}
                  getOptionLabel={(option) =>
                    option
                      ? `${option?.vehicle_id} - ${option?.registration_number}`
                      : ""
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={vehiclesList}
                  loading={
                    isContractor ? loadingContractorData : loadingDrivers
                  }
                  loadingText="Loading Vehicles..."
                  helperText={errors?.vehicle_id?.message}
                  error={!!errors?.vehicle_id}
                />
              )}
            />
          </Grid>

          <Grid item md={6}>
            <Controller
              name="actualPickupTime"
              control={control}
              rules={{ required: "Please select the actual pickup time." }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  required
                  label="Actual Pickup Time"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  helperText={errors?.actualPickupTime?.message}
                  error={!!errors?.actualPickupTime}
                />
              )}
            />
          </Grid>

          {!isContractor && (
            <Grid item md={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch
                    {...field}
                    label="Active"
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button variant="contained" onClick={handleSubmit(handleOnSubmit)}>
          Submit
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={() => navigate(-1)}
          disabled={isBusy}
        >
          Cancel
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default CollectionModalForm;
